import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import {
    LayoutDashboard,
    Package,
    ShoppingCart,
    BarChart3,
    Settings,
    LogOut,
    Bell,
    Menu,
    X,
    User,
    Users,
    ShoppingBag
} from 'lucide-react';
import { useUser } from '../../context/UserContext';
import orderService from '../../services/orderService';
import Toast from '../common/Toast';
import logo from '../../assets/images/logo2.jpeg';

const AdminLayout = () => {
    const { logout, user } = useUser();
    const location = useLocation();
    const navigate = useNavigate();
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [notifications, setNotifications] = useState([]);
    const [showNotifications, setShowNotifications] = useState(false);
    const [toast, setToast] = useState(null);

    // Polling for new orders every 30 seconds
    useEffect(() => {
        const checkNewOrders = async () => {
            try {
                const orders = await orderService.getAllOrders();

                // Get previously seen order IDs from localStorage
                const seenOrders = JSON.parse(localStorage.getItem('admin_seen_orders') || '[]');

                // Find orders not in the seen list
                const newOrders = orders.filter(o => !seenOrders.includes(o.id));

                if (newOrders.length > 0) {
                    // Update notifications state
                    const newNotifications = newOrders.map(o => ({
                        id: o.id,
                        customer: o.full_name,
                        time: new Date(o.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                        isRead: false
                    }));

                    setNotifications(prev => [...newNotifications, ...prev].slice(0, 5));

                    // Show toast for the most recent one if it's just one, otherwise a summary
                    if (newOrders.length === 1) {
                        setToast({ message: `Nouvelle commande ! #${newOrders[0].id} de ${newOrders[0].full_name}`, type: 'success' });
                    } else {
                        setToast({ message: `${newOrders.length} nouvelles commandes reçues !`, type: 'success' });
                    }

                    // Update seen orders in localStorage
                    const updatedSeen = [...seenOrders, ...newOrders.map(o => o.id)];
                    localStorage.setItem('admin_seen_orders', JSON.stringify(updatedSeen.slice(-100))); // Keep last 100
                }
            } catch (err) {
                console.error('Error polling for orders:', err);
            }
        };

        // Initial check
        checkNewOrders();

        const interval = setInterval(checkNewOrders, 30000); // 30 seconds
        return () => clearInterval(interval);
    }, []);

    const markAllAsRead = () => {
        setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
    };

    const menuItems = [
        { icon: LayoutDashboard, label: 'Tableau de bord', path: '/admin' },
        { icon: Package, label: 'Inventaire', path: '/admin/inventory' },
        { icon: ShoppingCart, label: 'Commandes', path: '/admin/orders' },
        { icon: Users, label: 'Administrateurs', path: '/admin/users' },
        { icon: BarChart3, label: 'Rapports', path: '/admin/reports' },
    ];

    const handleLogout = () => {
        logout();
        navigate('/auth');
    };

    return (
        <div className="min-h-screen bg-slate-50 flex">
            {/* Sidebar */}
            <aside
                className={`fixed inset-y-0 left-0 z-50 w-72 bg-green-950 text-white transition-transform duration-300 transform ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
                    } lg:relative lg:translate-x-0 shadow-2xl`}
            >
                <div className="h-full flex flex-col p-8">
                    {/* Logo Section */}
                    <Link to="/" className="mb-12 flex items-center gap-3">
                        <div className="bg-white/10 p-2 rounded-xl backdrop-blur-sm">
                            <img src={logo} alt="Logo" className="h-10 w-auto" />
                        </div>
                        <span className="text-xl font-black tracking-tighter uppercase italic">Showroom</span>
                    </Link>

                    {/* Navigation */}
                    <nav className="flex-grow space-y-2">
                        {menuItems.map((item) => (
                            <Link
                                key={item.path}
                                to={item.path}
                                className={`flex items-center gap-4 px-6 py-4 rounded-2xl font-bold transition-all ${location.pathname === item.path
                                    ? 'bg-brand text-white shadow-lg shadow-brand/20'
                                    : 'text-white/60 hover:text-white hover:bg-white/5'
                                    }`}
                            >
                                <item.icon className="w-5 h-5" />
                                {item.label}
                            </Link>
                        ))}
                    </nav>

                    {/* Footer Actions */}
                    <div className="mt-auto space-y-2 border-t border-white/10 pt-8">
                        <button
                            className="w-full flex items-center gap-4 px-6 py-4 rounded-2xl font-bold text-white/60 hover:text-white hover:bg-white/5 transition-all"
                        >
                            <Settings className="w-5 h-5" />
                            Paramètres
                        </button>
                        <button
                            onClick={handleLogout}
                            className="w-full flex items-center gap-4 px-6 py-4 rounded-2xl font-bold text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-all"
                        >
                            <LogOut className="w-5 h-5" />
                            Déconnexion Admin
                        </button>
                    </div>
                </div>
            </aside>

            {/* Main Content Area */}
            <div className="flex-grow flex flex-col min-h-screen overflow-x-hidden">
                {/* Top Header */}
                <header className="h-20 bg-white border-b border-slate-200 flex items-center justify-between px-8 sticky top-0 z-40">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                            className="p-2 hover:bg-slate-100 rounded-xl lg:hidden text-slate-500"
                        >
                            {isSidebarOpen ? <X /> : <Menu />}
                        </button>
                        <h2 className="text-xl font-extrabold text-green-950">
                            {menuItems.find(item => item.path === location.pathname)?.label || 'Administration'}
                        </h2>
                    </div>

                    <div className="flex items-center gap-6">
                        <div className="relative">
                            <button
                                onClick={() => {
                                    setShowNotifications(!showNotifications);
                                    if (!showNotifications) markAllAsRead();
                                }}
                                className="relative w-12 h-12 bg-slate-100 rounded-2xl flex items-center justify-center text-slate-500 hover:bg-slate-200 transition-all"
                            >
                                <Bell className="w-5 h-5" />
                                {notifications.some(n => !n.isRead) && (
                                    <span className="absolute top-3 right-3 w-2 h-2 bg-red-500 rounded-full border-2 border-white animate-pulse"></span>
                                )}
                            </button>

                            {/* Notifications Dropdown */}
                            {showNotifications && (
                                <div className="absolute right-0 mt-4 w-80 bg-white rounded-[2rem] shadow-2xl border border-slate-100 overflow-hidden z-[60] animate-in fade-in slide-in-from-top-2 duration-200">
                                    <div className="p-6 border-b border-slate-50 flex justify-between items-center bg-slate-50/50">
                                        <h3 className="font-black text-green-950 text-xs uppercase tracking-widest italic">Notifications</h3>
                                        {notifications.filter(n => !n.isRead).length > 0 && (
                                            <span className="px-2 py-1 bg-brand text-white text-[8px] font-black rounded-full uppercase tracking-tighter">
                                                {notifications.filter(n => !n.isRead).length} Nouvelles
                                            </span>
                                        )}
                                    </div>
                                    <div className="max-h-[350px] overflow-y-auto">
                                        {notifications.length > 0 ? (
                                            notifications.map((notif, idx) => (
                                                <div
                                                    key={idx}
                                                    className="p-4 hover:bg-slate-50 border-b border-slate-50 last:border-0 transition-colors cursor-pointer group"
                                                    onClick={() => {
                                                        setShowNotifications(false);
                                                        navigate('/admin/orders');
                                                    }}
                                                >
                                                    <div className="flex items-start gap-4">
                                                        <div className="w-10 h-10 rounded-xl bg-brand/10 text-brand flex items-center justify-center shrink-0 group-hover:bg-brand group-hover:text-white transition-all duration-300">
                                                            <ShoppingBag className="w-5 h-5" />
                                                        </div>
                                                        <div className="space-y-1">
                                                            <div className="flex items-center gap-2">
                                                                <p className="text-xs font-black text-green-950">
                                                                    #{notif.id}
                                                                </p>
                                                                {!notif.isRead && <span className="w-1.5 h-1.5 bg-brand rounded-full"></span>}
                                                            </div>
                                                            <p className="text-[11px] font-bold text-slate-600 line-clamp-1">{notif.customer}</p>
                                                            <p className="text-[9px] text-slate-400 font-black uppercase tracking-widest">
                                                                Il y a peu • {notif.time}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="p-12 text-center">
                                                <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4 border border-dashed border-slate-200">
                                                    <Bell className="w-6 h-6 text-slate-300" />
                                                </div>
                                                <p className="text-xs font-bold text-slate-400 italic">Tout est à jour !</p>
                                            </div>
                                        )}
                                    </div>
                                    <div className="p-4 bg-slate-50/80 text-center border-t border-slate-100">
                                        <Link
                                            to="/admin/orders"
                                            className="text-[10px] font-black text-brand uppercase tracking-widest hover:tracking-[0.15em] transition-all"
                                            onClick={() => setShowNotifications(false)}
                                        >
                                            Voir toutes les commandes
                                        </Link>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="flex items-center gap-4 pl-6 border-l border-slate-200">
                            <div className="text-right hidden sm:block">
                                <p className="text-sm font-black text-green-950">{user?.full_name || user?.name || user?.username || 'Administrateur'}</p>
                                <p className="text-[10px] font-bold text-brand uppercase tracking-widest">Admin Global</p>
                            </div>
                            <div className="w-12 h-12 rounded-2xl bg-brand/10 border-2 border-brand/20 flex items-center justify-center text-brand overflow-hidden shadow-inner">
                                {user?.avatar ? (
                                    <img src={user.avatar} alt="Admin" className="w-full h-full object-cover" />
                                ) : (
                                    <User className="w-6 h-6" />
                                )}
                            </div>
                        </div>
                    </div>
                </header>

                {/* Page Content */}
                <main className="p-8 pb-20 max-w-[1600px] mx-auto w-full">
                    <Outlet />
                </main>

                {toast && (
                    <Toast
                        message={toast.message}
                        type={toast.type}
                        onClose={() => setToast(null)}
                    />
                )}
            </div>
        </div>
    );
};

export default AdminLayout;
